import { t } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';
import { Trans } from '@lingui/react/macro';
import { useEffect, useId, useRef } from 'react';
import ManagedAgentProvisioningStatus, { SetupStatusIcon } from './ManagedAgentProvisioningStatus';
import styles from './ManagedAgentProvisioningStatus.module.css';

interface Props {
  agentId: string;
  ownerId: string | null;
  agentName: string;
  computerName: string;
  providerName: string;
  onClose: () => void;
  onViewAgent: () => void;
  onOpenChat: () => void;
}

export default function HostedAgentCreated({
  agentId,
  ownerId,
  agentName,
  computerName,
  providerName,
  onClose,
  onViewAgent,
  onOpenChat,
}: Props) {
  const { i18n } = useLingui();
  const titleId = useId();
  const dialog = useRef<HTMLDivElement>(null);
  const close = useRef(onClose);
  close.current = onClose;

  useEffect(() => {
    const previousFocus = document.activeElement;
    dialog.current?.querySelector<HTMLButtonElement>('button')?.focus();
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        close.current();
        return;
      }
      if (event.key !== 'Tab') return;
      const items = dialog.current?.querySelectorAll<HTMLElement>(
        'button:not(:disabled), summary, [href], [tabindex="0"]',
      );
      if (!items?.length) return;
      const first = items[0]!;
      const last = items[items.length - 1]!;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('keydown', handleKey);
      if (previousFocus instanceof HTMLElement && previousFocus.isConnected) previousFocus.focus();
    };
  }, []);

  return (
    <div className="admin-modal" onClick={onClose}>
      <div
        ref={dialog}
        className="admin-modal-content"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        style={{ width: 400, maxWidth: '90vw' }}
      >
        <header className={styles.modalHeading}>
          <h3 id={titleId}>
            <Trans>Agent created</Trans>
          </h3>
          <button
            type="button"
            className={styles.closeButton}
            aria-label={t(i18n)`Close`}
            onClick={onClose}
          >
            ×
          </button>
        </header>
        <div className={styles.savedAgent}>
          <SetupStatusIcon name="check" />
          <div>
            <Trans>
              <b>{agentName}</b> is saved in your workspace.
              <small>
                {computerName || t(i18n)`Your computer`} · {providerName}
              </small>
            </Trans>
          </div>
        </div>
        <ManagedAgentProvisioningStatus
          agentId={agentId}
          ownerId={ownerId}
          agentName={agentName}
          variant="creation"
          onClose={onClose}
          onViewAgent={onViewAgent}
          onOpenChat={onOpenChat}
        />
      </div>
    </div>
  );
}
